/**
 * Land Health trajectory (handoff 54 / 39). Print RB's five words as sent.
 * Supersedes handoff 07: no anomalous-season set, no "Improving" / "Declining"
 * / "broadly steady". A lean (Steady, rising / Steady, falling) never takes
 * the up or down colour.
 */

import { TRAJECTORY_COLORS } from './edxDescriptions';

export const TRAJECTORY_WORDS = [
  'Regenerating',
  'Steady, rising',
  'Steady',
  'Steady, falling',
  'Degrading',
];

const WORD_BY_CODE = {
  REGENERATING: 'Regenerating',
  'STEADY, RISING': 'Steady, rising',
  STEADY_RISING: 'Steady, rising',
  STEADY: 'Steady',
  'STEADY, FALLING': 'Steady, falling',
  STEADY_FALLING: 'Steady, falling',
  DEGRADING: 'Degrading',
};

const WORD_BY_LOWER = {
  regenerating: 'Regenerating',
  'steady, rising': 'Steady, rising',
  'steady rising': 'Steady, rising',
  steady: 'Steady',
  'steady, falling': 'Steady, falling',
  'steady falling': 'Steady, falling',
  degrading: 'Degrading',
};

function lsqSlope(years, vals) {
  const n = years.length;
  if (n < 2) return 0;
  const xm = years.reduce((a, b) => a + b, 0) / n;
  const ym = vals.reduce((a, b) => a + b, 0) / n;
  let den = 0;
  for (const y of years) den += (y - xm) ** 2;
  if (den === 0) return 0;
  let num = 0;
  for (let i = 0; i < n; i++) num += (years[i] - xm) * (vals[i] - ym);
  return num / den;
}

export function meanByYearFromSeries(yearlyData) {
  const out = {};
  if (!yearlyData) return out;
  if (Array.isArray(yearlyData)) {
    for (const row of yearlyData) {
      const y = row.year ?? row.YEAR;
      const v = row.ehi_pct ?? row.ehiPct ?? row.mean_ehi_pct;
      if (y != null && v != null && !Number.isNaN(Number(v))) out[Number(y)] = Number(v);
    }
  } else if (typeof yearlyData === 'object') {
    for (const [y, v] of Object.entries(yearlyData)) {
      if (v != null && !Number.isNaN(Number(v))) out[Number(y)] = Number(v);
    }
  }
  return out;
}

const MINUS = '\u2212';

function formatSigned(rate, digits) {
  const n = Number(rate);
  const abs = Math.abs(n).toFixed(digits);
  if (n < 0) return `${MINUS}${abs}`;
  if (n > 0) return `+${abs}`;
  return abs;
}

/**
 * Handoff 61. A published trend rate is already on the 1-to-9 scale. Print it
 * at two decimals and do not rescale it. `scaleName` is "Land Health" or a
 * process name such as "Water Cycle".
 */
export function formatPublishedLevelRate(rate, scaleName = 'Land Health') {
  if (rate == null || Number.isNaN(Number(rate))) return null;
  return `${formatSigned(rate, 2)} a year on the 1 to 9 ${scaleName} scale`;
}

/** Handoff 61 per-field rate. The number is already on the 1-to-9 scale. */
export function formatPublishedFieldRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return null;
  return `${formatSigned(rate, 2)}/yr`;
}

/** Legacy EE fallback only. The number is still a 0-100 index slope. */
export function formatLegacyPointRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return null;
  return `${formatSigned(rate, 1)} pts/yr`;
}

/**
 * Chip suffix beside the trend word. A published sentence (`trend_note`) is
 * the rate the client reads, so the chip stays empty and does not invent a
 * second unit. A bare published number uses the 1-to-9 form. The legacy
 * composer (`rate_scale: 'ehi_points'`) keeps its own unit.
 */
export function trajectoryChipRate(trajectory) {
  if (!trajectory || typeof trajectory === 'string') return null;
  if (trajectoryTrendNote(trajectory)) return null;
  const rate = trajectoryRatePerYear(trajectory);
  if (rate == null) return null;
  if (trajectory.rate_scale === 'ehi_points') return formatLegacyPointRate(rate);
  return formatPublishedLevelRate(rate, 'Land Health');
}

/** Legacy EE-only fallback. Published RB farms must not use this. */
export function computeTrajectory(meanByYear, { recentWindow = 5, thresh = 1.5, lean = 0.5 } = {}) {
  const insufficient = {
    trajectory: 'INSUFFICIENT_DATA',
    rate_per_year: null,
    baseline_label: 'STEADY',
    baseline_per_yr: 0,
    trend_note: '',
    anomaly_year: null,
    recent_delta: null,
    confidence: 'none',
    years_analyzed: 0,
  };
  if (!meanByYear || typeof meanByYear !== 'object') return insufficient;

  const vals = {};
  for (const [y, v] of Object.entries(meanByYear)) {
    if (v != null && v !== '' && !Number.isNaN(Number(v))) vals[Number(y)] = Number(v);
  }
  const years = Object.keys(vals).map(Number).sort((a, b) => a - b);
  if (years.length < 2) return { ...insufficient, years_analyzed: years.length };

  const lab = (s) => {
    if (s > thresh) return 'REGENERATING';
    if (s < -thresh) return 'DEGRADING';
    if (s > lean) return 'STEADY, RISING';
    if (s < -lean) return 'STEADY, FALLING';
    return 'STEADY';
  };
  const latest = years[years.length - 1];
  const recent_delta = Math.round((vals[years[years.length - 1]] - vals[years[years.length - 2]]) * 10) / 10;

  const bslope = years.length >= 2 ? lsqSlope(years, years.map((y) => vals[y])) : 0;
  const baseline_per_yr = Math.round(bslope * 10) / 10;
  const baseline_label = lab(bslope);

  const recYears = years.slice(-recentWindow);
  const rec = recYears.length >= 2 ? recYears : years;
  const rslope = rec.length >= 2 ? lsqSlope(rec, rec.map((y) => vals[y])) : bslope;
  const rate_per_year = Math.round(rslope * 10) / 10;
  const trajectory = lab(rslope);
  const word = WORD_BY_CODE[trajectory] || 'Steady';
  const baseWord = WORD_BY_CODE[baseline_label] || 'Steady';

  let note = `Long term the land has been ${baseWord}`;
  if (years.length >= 2) note += ` (${years[0]} to ${years[years.length - 1]})`;
  note += '; in the most recent years';
  if (rec.length >= 2) note += ` (${rec[0]} to ${rec[rec.length - 1]})`;
  note += ` it has been ${word} (${rate_per_year >= 0 ? '+' : ''}${rate_per_year.toFixed(1)} pts/yr on the whole-site mean).`;

  return {
    trajectory,
    trajectory_word: word,
    rate_per_year,
    baseline_label,
    baseline_per_yr,
    trend_note: note,
    anomaly_year: null,
    recent_delta,
    confidence: years.length >= 4 ? 'medium' : 'low',
    years_analyzed: years.length,
    // This slope is still on the 0-100 index. Do not present it as a 1-to-9 rate.
    rate_scale: 'ehi_points',
  };
}

/** Prefer the published RB trajectory. Recompute only when none is published. */
export function resolveFarmTrajectory(summary, fallbackYearlyData = null) {
  const fromApi = summary?.trajectory;
  if (fromApi && (fromApi.trajectory || fromApi.trajectory_word || fromApi.trend_note)) {
    return fromApi;
  }
  const yearly = fallbackYearlyData || summary?.yearlyData;
  const computed = computeTrajectory(meanByYearFromSeries(yearly));
  if (fromApi?.trajectory && computed.trajectory === 'INSUFFICIENT_DATA') return fromApi;
  return computed;
}

export function trajectoryDirection(trajectory) {
  if (!trajectory) return null;
  if (typeof trajectory === 'string') return trajectory;
  return trajectory.trajectory_word || trajectory.trajectory || trajectory.ehi_trend_label || null;
}

export function trajectoryDisplayLabel(code) {
  const dir = trajectoryDirection(code);
  if (!dir || dir === 'INSUFFICIENT_DATA') return null;
  if (WORD_BY_CODE[dir]) return WORD_BY_CODE[dir];
  const lower = String(dir).trim().toLowerCase();
  if (WORD_BY_LOWER[lower]) return WORD_BY_LOWER[lower];
  if (TRAJECTORY_WORDS.includes(dir)) return dir;
  return dir;
}

export function trajectoryTone(code) {
  const word = trajectoryDisplayLabel(code);
  if (word === 'Regenerating') return 'up';
  if (word === 'Degrading') return 'down';
  return 'steady';
}

export function trajectoryTrendNote(trajectory) {
  if (!trajectory || typeof trajectory === 'string') return '';
  return trajectory.trend_note || trajectory.ehi_trend_note || trajectory.window_text || trajectory.trajectory_window_text || '';
}

export function trajectoryChipColor(code) {
  const tone = trajectoryTone(code);
  if (tone === 'up') return TRAJECTORY_COLORS.REGENERATING || '#3E9345';
  if (tone === 'down') return TRAJECTORY_COLORS.DEGRADING || '#B24D22';
  return TRAJECTORY_COLORS.STEADY || '#D9A72A';
}

export function trajectoryRatePerYear(trajectory) {
  if (!trajectory || typeof trajectory === 'string') return null;
  const v = trajectory.rate_per_year ?? trajectory.trajectory_per_yr ?? trajectory.ehi_trend_per_yr;
  return v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
}
